import { useContext, useEffect, useState } from 'react'

import { Text } from '@hhgtech/hhg-components/components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { theme } from '@hhgtech/hhg-components/misc'
import Cookies from 'js-cookie'
import { useIntl } from 'react-intl'

import { AdBlock } from 'components/molecules/adBlock'
import { FixedBottomAdBlock } from 'components/molecules/adBlock/deferFixedBottomAdBlock'
import { ExpandedCard } from 'components/molecules/expandedCard'
import { HealthToolBreadcrumb } from 'components/molecules/healthToolBreadcrumb'
import { ToolsBannerCardV2 } from 'components/molecules/toolsBannerCardV2'
import { ToolsInfoCard } from 'components/molecules/toolsInfoCard'
import BabyGrowthForm from 'components/organisms/babyGrowthForm'
import MobileBottomNavigationWrapper from 'components/organisms/mobileBottomNavigationWrapper'
import { HealthToolLayout } from 'components/templates/healthToolLayout'
import { NavigatorLayout } from 'components/templates/navigatorLayout'
import { domainLocales } from 'domainLocales'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { assetImagePath } from 'interfaces/constants'
import { BABY_GROWTH_DATA_KEY } from 'interfaces/constants/cookies'
import { FooterInfo } from 'interfaces/types'
import { BABY_GROWTH_ACTION } from 'state/babyGrowth/actions'
import { BabyGrowthContext } from 'state/babyGrowth/context'
import { getAdTag, getCountryAdTag, getLocaleCategoryAdTag } from 'utils/ads'

import { StyledBabyGrowthPage } from './index.styled'
import { StyledBMIInfo, StyledInfo } from '../bmi/index.styled'
import {
  StyledHealthTools,
  StyledSuggestedArticlesContainer,
} from '../index.styled'

export type BabyGrowthHomePageProps = {
  footerInfo?: FooterInfo
  dataNavMenuContent?: NavMenuContentType
} & Record<string, unknown>

const BabyGrowthHomePage = ({
  footerInfo,
  dataNavMenuContent,
}: BabyGrowthHomePageProps) => {
  const { formatMessage: f } = useIntl()
  const [openInfo, setOpenInfo] = useState(-1)

  const isMobile = useIsMobile()

  const { locale, asPath } = useRouterExtend()

  const {
    state: { babyGrowthContent },
    dispatch,
  } = useContext(BabyGrowthContext)

  useEffect(() => {
    Cookies.remove(BABY_GROWTH_DATA_KEY, {
      domain: `.${domainLocales[locale]}`,
    })
    dispatch({ type: BABY_GROWTH_ACTION.RE_CALCULATE })
  }, [])

  const babyGrowthInfo = {
    title: f({
      id: 'tools.info',
      defaultMessage: 'Info',
    }),
    items: babyGrowthContent?.information || [],
  }

  const adUnitPath = getAdTag({
    countryAdTag: getCountryAdTag(locale, {
      isMobile,
    }),
    contentAdTag: getLocaleCategoryAdTag(locale, asPath, [
      'health-tools/baby-growth',
    ]),
  })
  const enabledAd = !!babyGrowthContent?.enable_ad
  const pageItem = [
    {
      label: f({ id: 'healthtools.title' }),
      value: '/health-tools',
    },
    {
      label: babyGrowthContent?.title || '',
      value: '#',
    },
  ]
  return (
    <>
      <NavigatorLayout
        SEOData={{
          title: babyGrowthContent?.title,
          image: babyGrowthContent?.featuredImage,
          description: babyGrowthContent?.description,
          apiMetaData: babyGrowthContent?.metaTag,
          isIndexable: true,
          type: 'website',
        }}
        footerInfo={footerInfo}
        isSponsored={babyGrowthContent?.is_sponsored}
        sponsoredType="content"
        sponsoredBy={babyGrowthContent?.sponsor || undefined}
        dataNavMenuContent={dataNavMenuContent}
      >
        <StyledBabyGrowthPage>
          <StyledHealthTools>
            <ToolsBannerCardV2
              breadcrumbProps={{ pageItem }}
              icon={
                <img
                  className="health-tool-icon"
                  alt="health-tool-icon"
                  loading="lazy"
                  src={
                    babyGrowthContent?.featuredImage ||
                    `${assetImagePath}/toolsIconBabyGrowth.svg`
                  }
                />
              }
              heading={babyGrowthContent?.title || ''}
              description={babyGrowthContent?.description || ''}
              reviewBy={babyGrowthContent?.reviewBy}
              isMobile={isMobile}
            />
            <HealthToolLayout>
              <HealthToolLayout.LeftColumn>
                <>
                  <BabyGrowthForm />
                  {babyGrowthContent?.disclaimer &&
                    babyGrowthContent?.disclaimer !== '' && (
                      <StyledInfo style={{ paddingBottom: 24 }}>
                        {enabledAd && isMobile && (
                          <AdBlock
                            adUnitPath={adUnitPath}
                            className="top-ad"
                            size={[300, 250]}
                            divId={'div-gpt-ad-topbanner-mobile'}
                            pos={['TopBanner']}
                          />
                        )}
                        <ToolsInfoCard
                          icon={
                            <img
                              style={{
                                height: '16px',
                                width: '16px',
                              }}
                              src={`${assetImagePath}/info-icon.svg`}
                              alt=""
                              loading="lazy"
                            />
                          }
                          headingTitle={f({
                            id: 'tools.disclaimer',
                            defaultMessage: 'Disclaimer',
                          })}
                        >
                          <Text size="p2" color={theme.colors.gray600}>
                            {babyGrowthContent.disclaimer}
                          </Text>
                        </ToolsInfoCard>
                      </StyledInfo>
                    )}
                  {babyGrowthInfo &&
                    babyGrowthInfo.items &&
                    babyGrowthInfo.items.length > 0 && (
                      <StyledInfo>
                        <ToolsInfoCard
                          icon={
                            <img
                              style={{
                                height: '16px',
                                width: '16px',
                              }}
                              src={`${assetImagePath}/info-icon.svg`}
                              alt=""
                              loading="lazy"
                            />
                          }
                          headingTitle={babyGrowthInfo.title}
                        >
                          <StyledBMIInfo>
                            {babyGrowthInfo.items?.map((item, iItem) => (
                              <ExpandedCard
                                key={'info-' + iItem}
                                isExpand={openInfo === iItem}
                                onChange={(value: boolean) =>
                                  value ? setOpenInfo(iItem) : setOpenInfo(-1)
                                }
                                title={item.title}
                                contentString={item.description}
                                iconPosition="right"
                                isMobile={isMobile}
                              />
                            ))}
                          </StyledBMIInfo>
                        </ToolsInfoCard>
                      </StyledInfo>
                    )}
                </>
              </HealthToolLayout.LeftColumn>
              <HealthToolLayout.RightColumn>
                {enabledAd && !isMobile && (
                  <StyledSuggestedArticlesContainer>
                    <AdBlock
                      adUnitPath={adUnitPath}
                      size={[
                        [300, 250],
                        [300, 600],
                      ]}
                      divId={'div-gpt-ad-topbanner-desktop'}
                      pos={['TopBanner']}
                    />
                  </StyledSuggestedArticlesContainer>
                )}
              </HealthToolLayout.RightColumn>
            </HealthToolLayout>
            {enabledAd && (
              <FixedBottomAdBlock
                adUnitPath={adUnitPath}
                className="article-bottom-ad"
                isMobile={isMobile}
              />
            )}
            {isMobile && <HealthToolBreadcrumb pageItem={pageItem} />}
          </StyledHealthTools>
        </StyledBabyGrowthPage>
      </NavigatorLayout>
      <MobileBottomNavigationWrapper.FitnessHealthTool appTool="baby-growth" />
    </>
  )
}

export default BabyGrowthHomePage
